import { find, sortBy } from "lodash";
import { startTransition, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE } from "src/helpers/constants";
import { sleep } from "src/helpers/utils";

export default function useLanguages() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LANGUAGE;
  const [isChangingLanguage, setIsChangingLanguage] = useState<boolean>(false);

  const allLanguages = useMemo(() => {
    return sortBy(
      [
        {
          id: "es",
          name: t("modals:language_change.spanish.name"),
          description: t("modals:language_change.spanish.description"),
        },
        {
          id: "en",
          name: t("modals:language_change.english.name"),
          description: t("modals:language_change.english.description"),
        },
        {
          id: "pt",
          name: t("modals:language_change.portuguese.name"),
          description: t("modals:language_change.portuguese.description"),
        },
        {
          id: "zh",
          name: t("modals:language_change.mandarin.name"),
          description: t("modals:language_change.mandarin.description"),
        },
        {
          id: "hi",
          name: t("modals:language_change.hindi.name"),
          description: t("modals:language_change.hindi.description"),
        },
      ],
      ["name"],
    );
  }, [lang, t]);

  const currentLanguageId = useMemo(() => {
    return lang.split("-")[0];
  }, [lang]);

  const currentLanguageData = useMemo(() => {
    return find(allLanguages, ({ id }) => id === currentLanguageId);
  }, [currentLanguageId, allLanguages]);

  const changeLanguage = async (lang: string) => {
    try {
      setIsChangingLanguage(true);
      await i18n.changeLanguage(lang);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  const allLanguagesSelect = useMemo(() => {
    return allLanguages?.map?.((lang: any) => ({
      value: lang.id,
      label: lang.name,
      // description: lang.description,
    }));
  }, [allLanguages]);

  return {
    allLanguages,
    currentLanguageId,
    currentLanguageData,
    changeLanguage,
    allLanguagesSelect,
    isChangingLanguage,
  };
}
